import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en.json';
import es from '../locales/es.json';
import it from '../locales/it.json';
import fr from '../locales/fr.json';
import de from '../locales/de.json';
import pt from '../locales/pt.json';
import ru from '../locales/ru.json';
import zh from '../locales/zh.json';
import ja from '../locales/ja.json';
import ko from '../locales/ko.json';
import ar from '../locales/ar.json';
import hi from '../locales/hi.json';
import nl from '../locales/nl.json';

const locales = { en, es, it, fr, de, pt, ru, zh, ja, ko, ar, hi, nl };

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Try to get language from URL parameter first, then localStorage, default to 'en'
  const [language, setLanguage] = useState(() => {
    // Check URL parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const urlLanguage = urlParams.get('language');
    
    // If URL has a valid language parameter, use it
    if (urlLanguage && locales[urlLanguage]) {
      return urlLanguage;
    }
    
    // Otherwise, try localStorage
    const savedLanguage = localStorage.getItem('certage_language');
    return savedLanguage || 'en';
  });

  // Check for URL language parameter on mount and update if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLanguage = urlParams.get('language');
    
    // If URL has a valid language parameter, override current language
    if (urlLanguage && locales[urlLanguage] && urlLanguage !== language) {
      setLanguage(urlLanguage);
    }
  }, []);

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('certage_language', language);
  }, [language]);

  // Fallback to English if selected language is not available
  const t = locales[language] || locales.en;

  const value = {
    language,
    setLanguage,
    t,
    locales
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};